import { Link, createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';

import { GRONDSLAGEN_MAP } from '@/constants/grondslagen';
import { DateUtils } from '@/utils/dateUtils';

import { ChapterNavigation } from '../-components/ChapterNavigation';
import { NoFilterResultWarning } from '../-components/NoFilterResultWarning';
import { NoSelectedLabelWarning } from '../-components/NoSelectedLabelWarning';
import useViewerStore, { SidebarLeftTab } from '../-utils/useViewerStore';
import { VigerendContentHeader } from './-components/VigerendContentHeader';
import { useVigerendDataContext } from './-components/VigerendDataContext';
import { VigerendObjectList } from './-components/VigerendObjectList';
import { VigerendPastWarning } from './-components/VigerendPastWarning';
import { useFindHoofdstukInVigerendData } from './-utils/useFindHoofdstukInVigerendData';
import { VigerendHoofdstuk } from './-utils/useVigerendData';

export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/vigerend/labels/$hoofdstukLineageId'
)({
  validateSearch: z.object({
    datum: z.string().date().catch(DateUtils.formatForInput(new Date())),
  }),
  onEnter: () => {
    useViewerStore.getState().setSidebarLeftTabId(SidebarLeftTab.FILTER);
  },
  component: LabelsComponent,
});

function LabelsComponent() {
  const { bedrijfLineageId, hoofdstukLineageId } = Route.useParams();
  const { datum } = Route.useSearch();
  const datumObject = new Date(datum);

  const { filteredVigerendData } = useVigerendDataContext();

  const {
    hoofdstuk: currentHoofdstuk,
    nextHoofdstuk,
    previousHoofdstuk,
  } = useFindHoofdstukInVigerendData({
    vigerendData: filteredVigerendData,
    hoofdstukLineageId: hoofdstukLineageId,
  });

  return (
    <>
      <VigerendPastWarning
        date={datumObject}
        bedrijfLineageId={bedrijfLineageId}
        hoofdstukLineageId={hoofdstukLineageId}
        view="labels"
      />

      <VigerendContentHeader date={datumObject} />

      <VigerendContent
        bedrijfLineageId={bedrijfLineageId}
        hoofdstukLineageId={hoofdstukLineageId}
        datum={datum}
        currentHoofdstuk={currentHoofdstuk}
      />

      <ChapterNavigation
        previous={
          previousHoofdstuk?.data.ID && (
            <ChapterNavigation.Previous
              subtitle="Vorig hoofdstuk"
              grondslag={GRONDSLAGEN_MAP.get(previousHoofdstuk.data.Grondslag)}
              nummer={previousHoofdstuk.data.Nummer}
              htmlTitle={previousHoofdstuk.data.Titel}
              to="/$bedrijfLineageId/viewer/vigerend/labels/$hoofdstukLineageId"
              params={{
                bedrijfLineageId,
                hoofdstukLineageId: previousHoofdstuk.data.Lineage,
              }}
              search={{ datum }}
            />
          )
        }
        next={
          nextHoofdstuk?.data.ID && (
            <ChapterNavigation.Next
              grondslag={GRONDSLAGEN_MAP.get(nextHoofdstuk.data.Grondslag)}
              subtitle="Volgend hoofdstuk"
              nummer={nextHoofdstuk.data.Nummer}
              htmlTitle={nextHoofdstuk.data.Titel}
              to="/$bedrijfLineageId/viewer/vigerend/labels/$hoofdstukLineageId"
              params={{
                bedrijfLineageId,
                hoofdstukLineageId: nextHoofdstuk.data.Lineage,
              }}
              search={{ datum }}
            />
          )
        }
      />
    </>
  );
}

const VigerendContent = ({
  bedrijfLineageId,
  hoofdstukLineageId,
  datum,
  currentHoofdstuk,
}: {
  bedrijfLineageId: string;
  hoofdstukLineageId: string;
  datum: string;
  currentHoofdstuk?: VigerendHoofdstuk;
}) => {
  const hasSelectedLabels = Route.useSearch({
    select: (search) => !!search.labelIds?.length,
  });

  const renderLinkSlot = useCallback(
    (paragraafLineageId: string) => (
      <Link
        to="/$bedrijfLineageId/viewer/vigerend/inhoud/$hoofdstukLineageId"
        params={{ bedrijfLineageId, hoofdstukLineageId }}
        search={{ datum, focusParagraafLineageId: paragraafLineageId }}
      >
        Bekijk ongefilterde paragraaf
      </Link>
    ),
    [bedrijfLineageId, hoofdstukLineageId, datum]
  );

  if (!hasSelectedLabels) {
    return <NoSelectedLabelWarning className="mt-3" />;
  }

  if (!currentHoofdstuk) {
    return <NoFilterResultWarning className="mt-3" />;
  }

  return (
    <VigerendObjectList
      vigerendHoofdstuk={currentHoofdstuk}
      renderLinkSlot={renderLinkSlot}
    />
  );
};
